import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, User, FileText, Edit, Calendar, Phone, Mail, MapPin, UserCheck, CreditCard, X, AlertCircle, Plus, Minus, RotateCcw, CheckCircle } from 'lucide-react';
import patientService from '../../patient/services/patientService';
import apiClient from '../../../services/apiClient';

const AdminPatientFilesPage = () => {
  const navigate = useNavigate();
  
  // State for search inputs
  const [searchData, setSearchData] = useState({
    patientId: '',
    patientName: '',
    patientDOB: ''
  });

  // State for patient search and selection
  const [showPatientIdDropdown, setShowPatientIdDropdown] = useState(false);
  const [showPatientNameDropdown, setShowPatientNameDropdown] = useState(false);
  const [filteredPatientsById, setFilteredPatientsById] = useState([]);
  const [filteredPatientsByName, setFilteredPatientsByName] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isPatientSelected, setIsPatientSelected] = useState(false);

  // State for real patient data from API
  const [allPatients, setAllPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);

  // State for toast notifications
  const [toast, setToast] = useState(null);
  const [toasts, setToasts] = useState([]);

  // State for collapsible search section (open by default on Patient Files page)
  const [isSearchExpanded, setIsSearchExpanded] = useState(true);

  // Add Patient Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [addPatientData, setAddPatientData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    patient_type: 'Standard',
    address: '',
    suburb: '',
    state: '',
    postcode: '',
    country: 'Australia',
    height: '',
    weight: '',
    nok_name: '',
    nok_relation: '',
    nok_phone: '',
    nok_email: '',
    is_patient: true,
    is_active: true
  });
  const [addLoading, setAddLoading] = useState(false);

  // Set page title and fetch patients
  useEffect(() => {
    document.title = 'MediFusion - Admin Patient Files';
    fetchAllPatients();
  }, []);

  // Fetch all patients from the database
  const fetchAllPatients = async () => {
    try {
      setLoadingPatients(true);
      console.log('🔍 Fetching all patients from database...');
      const response = await patientService.getAllPatients();
      console.log('👥 Patients from API:', response);
      
      // The response should have a patients array and metadata
      const patients = response.patients || response || [];
      setAllPatients(patients);
      console.log('✅ Loaded', patients.length, 'patients from database');
    } catch (error) {
      console.error('❌ Error fetching patients:', error);
      // Fallback to empty array if API fails
      setAllPatients([]);
    } finally {
      setLoadingPatients(false);
    }
  };

  // Filter patients by ID
  const filterPatientsById = (searchTerm) => {
    if (!searchTerm.trim()) {
      setFilteredPatientsById([]);
      return;
    }

    const filtered = allPatients.filter(patient => 
      patient.patient_id && patient.patient_id.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPatientsById(filtered);
  };

  // Filter patients by name
  const filterPatientsByName = (searchTerm) => {
    if (!searchTerm.trim()) {
      setFilteredPatientsByName([]);
      return;
    }

    const filtered = allPatients.filter(patient => 
      `${patient.first_name} ${patient.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPatientsByName(filtered);
  };

  // Handle patient ID input change
  const handlePatientIdChange = (value) => {
    setSearchData(prev => ({ ...prev, patientId: value }));
    filterPatientsById(value);
    setShowPatientIdDropdown(true);
    setShowPatientNameDropdown(false);
  };

  // Handle patient name input change
  const handlePatientNameChange = (value) => {
    setSearchData(prev => ({ ...prev, patientName: value }));
    filterPatientsByName(value);
    setShowPatientNameDropdown(true);
    setShowPatientIdDropdown(false);
  };

  // Handle patient selection from dropdown
  const handlePatientSelect = (patient, searchType = 'mixed') => {
    setSelectedPatient(patient);
    
    // Format DOB for the date input field
    let formattedDOB = '';
    if (patient.dob && patient.dob !== '1900-01-01' && patient.dob !== null) {
      formattedDOB = patient.dob; // API already returns YYYY-MM-DD format
      console.log(`✅ DOB available for ${patient.first_name} ${patient.last_name}: ${patient.dob}`);
    } else {
      console.log(`⚠️ No valid DOB found for ${patient.first_name} ${patient.last_name}`);
    }
    
    // Update search data based on search type to prevent auto-population
    if (searchType === 'id') {
      // Patient ID search - only populate ID, don't auto-populate name/DOB
      setSearchData(prev => ({
        ...prev,
        patientId: patient.patient_id
        // Keep existing patientName and patientDOB values
      }));
    } else if (searchType === 'name') {
      // Patient name search - only populate name, don't auto-populate ID/DOB
      setSearchData(prev => ({
        ...prev,
        patientName: `${patient.first_name} ${patient.last_name}`
        // Keep existing patientId and patientDOB values
      }));
    } else {
      // Mixed/legacy behavior - populate all fields
      setSearchData({
        patientId: patient.patient_id,
        patientName: `${patient.first_name} ${patient.last_name}`,
        patientDOB: formattedDOB
      });
    }
    
    setIsPatientSelected(true);
    setShowPatientIdDropdown(false);
    setShowPatientNameDropdown(false);
    setFilteredPatientsById([]);
    setFilteredPatientsByName([]);
  };

  // Show toast notification
  const showToast = (message, type = 'error') => {
    setToast({ message, type });
    // Auto-hide toast after 4 seconds
    setTimeout(() => {
      setToast(null);
    }, 4000);
  };

  // Toast management for new toasts system
  const addToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      removeToast(id);
    }, 5000);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    
    // Dual search logic: Patient ID only OR Patient Name + DOB
    const hasPatientId = searchData.patientId.trim();
    const hasPatientName = searchData.patientName.trim();
    const hasPatientDOB = searchData.patientDOB.trim();
    
    // Search Method 1: Patient ID only
    if (hasPatientId && !hasPatientName && !hasPatientDOB) {
      console.log('🔍 Searching by Patient ID only:', searchData.patientId);
      
      // Find patient by ID
      const patient = allPatients.find(p => p.patient_id === searchData.patientId.trim());
      if (patient) {
        console.log('✅ Patient found by ID! Navigating to patient details...');
        navigate(`/admin/patientfiles/${patient.patient_id}/general_details`);
      } else {
        showToast('Patient not found with this ID', 'error');
      }
      return;
    }
    
    // Search Method 2: Patient Name + DOB combination
    if (hasPatientName && hasPatientDOB) {
      console.log('🔍 Searching by Patient Name + DOB:', { name: searchData.patientName, dob: searchData.patientDOB });
      
      // Find patient by name and DOB
      const patient = allPatients.find(p => {
        const fullName = `${p.first_name} ${p.last_name}`.toLowerCase();
        const nameMatch = fullName.includes(searchData.patientName.toLowerCase());
        const dobMatch = p.dob === searchData.patientDOB;
        return nameMatch && dobMatch;
      });
      
      if (patient) {
        console.log('✅ Patient found by Name + DOB! Navigating to patient details...');
        navigate(`/admin/patientfiles/${patient.patient_id}/general_details`);
      } else {
        showToast('Patient not found with this name and date of birth combination', 'error');
      }
      return;
    }
    
    // Invalid search combinations
    if (hasPatientId && (hasPatientName || hasPatientDOB)) {
      showToast('Please search by Patient ID only, or by Patient Name + DOB combination', 'error');
      return;
    }
    
    if (hasPatientName && !hasPatientDOB) {
      showToast('When searching by name, please also enter the date of birth', 'error');
      return;
    }
    
    if (!hasPatientId && !hasPatientName) {
      showToast('Please enter either a Patient ID or Patient Name to search', 'error');
      return;
    }
  };

  // Handle new patient button
  const handleNewPatient = () => {
    setSearchData({
      patientId: '',
      patientName: '',
      patientDOB: ''
    });
    setSelectedPatient(null);
    setIsPatientSelected(false);
    setShowPatientIdDropdown(false);
    setShowPatientNameDropdown(false);
    setFilteredPatientsById([]);
    setFilteredPatientsByName([]);
  };

  // Add Patient Modal Functions
  const openAddModal = () => {
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setAddPatientData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      date_of_birth: '',
      gender: '',
      patient_type: 'Standard',
      address: '',
      suburb: '',
      state: '',
      postcode: '',
      country: 'Australia',
      height: '',
      weight: '',
      nok_name: '',
      nok_relation: '',
      nok_phone: '',
      nok_email: '',
      is_patient: true,
      is_active: true
    });
  };

  const updateAddPatientData = (field, value) => {
    setAddPatientData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddPatient = async () => {
    try {
      setAddLoading(true);
      
      // Validate required fields
      if (!addPatientData.email) {
        addToast('Email is required to create a patient account.', 'error');
        return;
      }
      
      if (!addPatientData.first_name || !addPatientData.last_name) {
        addToast('First name and last name are required.', 'error');
        return;
      }

      if (!addPatientData.date_of_birth) {
        addToast('Date of birth is required.', 'error');
        return;
      }
      
      // Create new patient with user account via API
      const response = await apiClient.post('/users/create', {
        ...addPatientData,
        dob: addPatientData.date_of_birth, // Backend expects 'dob' field for patient record
        is_patient: true,
        is_active: true
      });
      
      // Refresh patients list
      await fetchAllPatients();
      
      // Close modal and reset form
      closeAddModal();
      
      // Show success message
      addToast('Patient created successfully! A user account has been created and credentials have been sent to the patient\'s email.', 'success');
      
    } catch (error) {
      console.error('Error creating patient:', error);
      
      // Check for specific error messages
      if (error.response?.data?.detail) {
        if (error.response.data.detail.includes('User with this email already exists')) {
          addToast('A user with this email already exists. Please use a different email address.', 'error');
        } else {
          addToast(`Failed to create patient: ${error.response.data.detail}`, 'error');
        }
      } else {
        addToast(`Failed to create patient: ${error.message}`, 'error');
      }
    } finally {
      setAddLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Top Banner */}
      <div className="bg-blue-600 text-white px-6 py-1.5 rounded-lg mb-6 flex justify-between items-center">
        <h1 className="text-lg font-semibold text-center flex-1">Patient Files</h1>
        <button
          onClick={openAddModal}
          className="bg-white text-blue-600 px-4 py-2 rounded-md hover:bg-blue-50 transition-colors duration-200 flex items-center space-x-2 shadow-sm text-sm font-medium"
        >
          <Plus className="h-4 w-4" />
          <span>Add Patient</span>
        </button>
      </div>

      {/* White Content Container */}
      <div className={`bg-white rounded-lg shadow-md ${isSearchExpanded ? "p-4" : "p-3"}`}>
        {/* Search Section */}
        <div className={isSearchExpanded ? "mb-4" : "mb-2"}>
          {/* Search Header with Icon and Toggle */}
          <div className={`flex items-center justify-between ${isSearchExpanded ? "mb-2" : "mb-0"}`}>
            <div className="flex items-center space-x-2">
              <Search className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-bold text-gray-800">Patient Search</h2>
            </div>
            <button
              type="button"
              onClick={() => setIsSearchExpanded(!isSearchExpanded)}
              className={`bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center justify-center transition-colors shadow-sm ${isSearchExpanded ? "w-8 h-8" : "w-7 h-7"}`}
            >
              {isSearchExpanded ? (
                <Minus className="h-4 w-4" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </button>
          </div>
          
          {/* Collapsible Content */}
          {isSearchExpanded && (
            <div>
              {/* Blue stylish divider */}
              <div className="h-0.5 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full mb-4"></div>
              
              {/* Search Form */}
              <form onSubmit={handleSearch} className="space-y-2">
            {/* Input Fields and Buttons Row - All in One Line */}
            <div className="flex items-end space-x-3">
              {/* Patient ID Input */}
              <div className="flex-1 relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Patient ID:</label>
                <input
                  type="text"
                  placeholder="Enter Patient ID"
                  value={searchData.patientId}
                  onChange={(e) => handlePatientIdChange(e.target.value)}
                  onFocus={() => setShowPatientIdDropdown(true)}
                  disabled={isPatientSelected}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                />
                
                {/* Dropdown for patient selection by ID */}
                {showPatientIdDropdown && filteredPatientsById.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                    {filteredPatientsById.map((patient) => (
                      <div
                        key={patient.id || patient.patient_id}
                        onClick={() => handlePatientSelect(patient, 'id')}
                        className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                      >
                        <div className="font-medium text-gray-900">{patient.patient_id}</div>
                        <div className="text-sm text-gray-600">{patient.first_name} {patient.last_name}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Patient Name Input */}
              <div className="flex-1 relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name:</label>
                <input
                  type="text"
                  placeholder="Enter Patient Name"
                  value={searchData.patientName}
                  onChange={(e) => handlePatientNameChange(e.target.value)}
                  onFocus={() => setShowPatientNameDropdown(true)}
                  disabled={isPatientSelected}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                />
                
                {/* Dropdown for patient selection by name */}
                {showPatientNameDropdown && filteredPatientsByName.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                    {filteredPatientsByName.map((patient) => (
                      <div
                        key={patient.id || patient.patient_id}
                        onClick={() => handlePatientSelect(patient, 'name')}
                        className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                      >
                        <div className="font-medium text-gray-900">{patient.first_name} {patient.last_name}</div>
                        <div className="text-sm text-gray-500">ID: {patient.patient_id}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Patient DOB Input */}
              <div className="w-48">
                <label className="block text-sm font-medium text-gray-700 mb-1">Patient DOB:</label>
                <input
                  type="date"
                  value={searchData.patientDOB}
                  onChange={(e) => setSearchData(prev => ({ ...prev, patientDOB: e.target.value }))}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm"
                />
              </div>
              
              {/* Search Button */}
              <button
                type="submit"
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 rounded-md transition-colors duration-200 flex items-center space-x-2 shadow-sm hover:shadow-md h-8 text-sm"
              >
                <Search className="h-3 w-3" />
                <span>Search</span>
              </button>
              
              {/* Reset Fields Button */}
              <button
                type="button"
                onClick={handleNewPatient}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-1.5 rounded-md transition-all duration-200 flex items-center space-x-2 shadow-sm hover:shadow-md h-8 text-sm"
              >
                <RotateCcw className="h-3 w-3" />
                <span>Reset Fields</span>
              </button>
            </div>
          </form>
            </div>
          )}
        </div>

        {/* Empty Display Area */}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
          <div className="text-gray-400 mb-4">
            <FileText className="h-16 w-16 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-500 mb-2">No Patient Files Found</h3>
          <p className="text-gray-400">Use the search above to find patient files or create a new patient</p>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3 transition-all duration-300 transform ${
          toast.type === 'error' 
            ? 'bg-red-500 text-white' 
            : 'bg-green-500 text-white'
        }`}>
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span className="font-medium">{toast.message}</span>
          <button
            onClick={() => setToast(null)}
            className="ml-2 hover:bg-black hover:bg-opacity-20 rounded-full p-1 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Add Patient Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Add New Patient</h2>
              <button
                onClick={closeAddModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6">
              <form onSubmit={(e) => { e.preventDefault(); handleAddPatient(); }}>
                {/* Personal Information */}
                <div className="mb-6">
                  <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    Personal Information
                  </h3>
                  
                  {/* First Row */}
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">First Name *</label>
                      <input
                        type="text"
                        value={addPatientData.first_name}
                        onChange={(e) => updateAddPatientData('first_name', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Last Name *</label>
                      <input
                        type="text"
                        value={addPatientData.last_name}
                        onChange={(e) => updateAddPatientData('last_name', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Email *</label>
                      <input
                        type="email"
                        value={addPatientData.email}
                        onChange={(e) => updateAddPatientData('email', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="text"
                        value={addPatientData.phone}
                        onChange={(e) => updateAddPatientData('phone', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Second Row */}
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Date of Birth *</label>
                      <input
                        type="date"
                        value={addPatientData.date_of_birth}
                        onChange={(e) => updateAddPatientData('date_of_birth', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Gender</label>
                      <select
                        value={addPatientData.gender}
                        onChange={(e) => updateAddPatientData('gender', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                        <option value="prefer_not_to_say">Prefer not to say</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Patient Type</label>
                      <select
                        value={addPatientData.patient_type}
                        onChange={(e) => updateAddPatientData('patient_type', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="Standard">Standard</option>
                        <option value="Aged Care">Aged Care</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Height (cm)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={addPatientData.height}
                        onChange={(e) => updateAddPatientData('height', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Third Row */}
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Weight (kg)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={addPatientData.weight}
                        onChange={(e) => updateAddPatientData('weight', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div className="mb-6">
                  <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                    <MapPin className="h-4 w-4 mr-2" />
                    Address Information
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Address</label>
                      <input
                        type="text"
                        value={addPatientData.address}
                        onChange={(e) => updateAddPatientData('address', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Suburb</label>
                      <input
                        type="text"
                        value={addPatientData.suburb}
                        onChange={(e) => updateAddPatientData('suburb', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">State</label>
                      <input
                        type="text"
                        value={addPatientData.state}
                        onChange={(e) => updateAddPatientData('state', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Postcode</label>
                      <input
                        type="text"
                        value={addPatientData.postcode}
                        onChange={(e) => updateAddPatientData('postcode', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Country</label>
                      <input
                        type="text"
                        value={addPatientData.country}
                        onChange={(e) => updateAddPatientData('country', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Next of Kin Information */}
                <div className="mb-6">
                  <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                    <UserCheck className="h-4 w-4 mr-2" />
                    Next of Kin Information
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">NOK Name</label>
                      <input
                        type="text"
                        value={addPatientData.nok_name}
                        onChange={(e) => updateAddPatientData('nok_name', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">NOK Relation</label>
                      <input
                        type="text"
                        value={addPatientData.nok_relation}
                        onChange={(e) => updateAddPatientData('nok_relation', e.target.value)}
                        placeholder="e.g., Spouse, Parent, Child"
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">NOK Phone</label>
                      <input
                        type="text"
                        value={addPatientData.nok_phone}
                        onChange={(e) => updateAddPatientData('nok_phone', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">NOK Email</label>
                      <input
                        type="email"
                        value={addPatientData.nok_email}
                        onChange={(e) => updateAddPatientData('nok_email', e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeAddModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addLoading || !addPatientData.first_name || !addPatientData.last_name || !addPatientData.email || !addPatientData.date_of_birth}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {addLoading ? 'Creating...' : 'Create Patient'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 space-y-2 z-50">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`flex items-center px-4 py-3 rounded-lg shadow-lg transform transition-all duration-300 ease-in-out ${
              toast.type === 'success'
                ? 'bg-green-500 text-white'
                : 'bg-red-500 text-white'
            }`}
          >
            {toast.type === 'success' ? (
              <CheckCircle className="h-5 w-5 mr-3 flex-shrink-0" />
            ) : (
              <AlertCircle className="h-5 w-5 mr-3 flex-shrink-0" />
            )}
            <span className="text-sm font-medium">{toast.message}</span>
            <button
              onClick={() => removeToast(toast.id)}
              className="ml-3 flex-shrink-0 text-white hover:text-gray-200 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminPatientFilesPage;

